/**
 * 评测报告（`#eval-reports`）：全量评测的完整分析报告（plan-batch-report skill 产出），仓库 `docs/plan-eval-reports/*.md` 只读同源。
 * 列表 → 点一行开抽屉：摘要、版本记录、渲染后的全文。变更走 PR。
 * 盲测保护（10-21 A1）：报告里提到盲测 ／ 留出题的段落，接口渲染时已换成「盲测题 `题号`：内容不展示」（历史 md 不改）；
 * 换掉过的报告在全文上方写一句「本报告有 n 处盲测题内容不展示」。
 */
import { useState } from "react";

import { Card, CardContent } from "@/components/ui/card";
import { Drawer, DrawerSection } from "@/components/ui/drawer";
import { EmptyState, LoadingInline } from "@/components/ui/feedback";
import {
	type PlanEvalReport,
	type PlanMethodSkillUpdate,
	planErrorMessage,
	usePlanEvalReports,
} from "@/lib/api/plan-lab";

import { FailState } from "../kit/fail-state";
import { Markdown } from "../kit/markdown";
import { PageJump } from "../kit/page-jump";
import { type PlanColumn, PlanTable } from "../kit/plan-table";
import { SEALED_CONTENT_TEXT } from "../kit/sealed-notice";

const COLUMNS: PlanColumn<PlanEvalReport>[] = [
	{
		key: "title",
		title: "报告",
		className: "max-w-[360px]",
		render: (r) => (
			<span className="block truncate font-medium" title={r.title}>
				{r.title}
			</span>
		),
	},
	{
		key: "date",
		title: "日期",
		className: "w-28",
		render: (r) => <span className="font-mono">{r.date || "—"}</span>,
	},
	{ key: "pass", title: "严格通过率", className: "w-28", render: (r) => r.pass_rate || "—" },
	{
		key: "batch",
		title: "批次 ／ 对照",
		className: "max-w-[280px]",
		render: (r) => (
			<span
				className="block truncate font-mono text-xs text-text-muted"
				title={`${r.batch_id} ／ ${r.compare}`}
			>
				{r.batch_id || "—"}
				{r.compare ? ` ／ ${r.compare}` : ""}
			</span>
		),
	},
	{ key: "versions", title: "版本", className: "w-16", render: (r) => r.versions },
	{
		key: "latest",
		title: "最近更新",
		className: "w-28",
		render: (r) => <span className="font-mono">{r.latest_update || "—"}</span>,
	},
];

const UPDATE_COLUMNS: PlanColumn<PlanMethodSkillUpdate>[] = [
	{
		key: "date",
		title: "日期",
		className: "w-28",
		render: (u) => <span className="font-mono">{u.date}</span>,
	},
	{
		key: "change",
		title: "改动（每行一版）",
		className: "max-w-[560px]",
		render: (u) => (
			<span className="block truncate text-text-secondary" title={u.change}>
				{u.change}
			</span>
		),
	},
	{
		key: "source",
		title: "出处",
		className: "max-w-[200px]",
		render: (u) => (
			<span className="block truncate text-text-muted" title={u.source}>
				{u.source}
			</span>
		),
	},
];

export function EvalReportsTab() {
	const q = usePlanEvalReports();
	const [open, setOpen] = useState<PlanEvalReport | null>(null);
	const rows = q.data?.reports ?? [];

	if (q.isLoading)
		return (
			<Card>
				<CardContent className="py-4">
					<LoadingInline />
				</CardContent>
			</Card>
		);
	if (q.isError)
		return (
			<Card>
				<CardContent>
					<FailState
						reason={planErrorMessage(q.error)}
						onRetry={() => q.refetch()}
						retrying={q.isFetching}
						testId="plan-eval-reports-fail"
					/>
				</CardContent>
			</Card>
		);

	return (
		<Card data-testid="plan-eval-reports">
			<CardContent className="space-y-3 py-4">
				<p className="text-xs text-text-muted">
					报告基于 <PageJump to="eval-records" /> 里的批次
				</p>
				{rows.length ? (
					<PlanTable
						columns={COLUMNS}
						rows={rows}
						rowKey={(r) => r.name}
						onRowClick={setOpen}
						rowTestId="plan-eval-report-row"
					/>
				) : (
					<EmptyState
						title="还没有评测报告"
						desc="全量评测跑完后按 plan-batch-report skill 出报告，放进仓库 docs/plan-eval-reports/。"
					/>
				)}
			</CardContent>
			<Drawer
				open={!!open}
				onClose={() => setOpen(null)}
				width="min(1080px,95vw)"
				testId="plan-eval-report-drawer"
				title={open?.title}
				description={
					open
						? `${open.path}${open.pass_rate ? ` · 严格通过率 ${open.pass_rate}` : ""}`
						: undefined
				}
			>
				{open ? (
					<>
						{open.summary || open.updates.length ? (
							<DrawerSection title="摘要与版本记录">
								{open.summary ? (
									<p className="text-sm text-text-secondary">{open.summary}</p>
								) : null}
								{open.updates.length ? (
									<PlanTable
										columns={UPDATE_COLUMNS}
										rows={open.updates}
										rowKey={(u) => `${u.date}|${u.change.slice(0, 40)}`}
										density="compact"
									/>
								) : null}
							</DrawerSection>
						) : null}
						<DrawerSection title="全文">
							{open.sealed_redacted ? (
								<p className="text-xs text-text-muted" data-testid="plan-eval-report-sealed">
									本报告有 {open.sealed_redacted} 处{SEALED_CONTENT_TEXT}（盲测 ／
									留出题只给题号与结论）
								</p>
							) : null}
							<Markdown text={open.body} testId="plan-eval-report-body" />
						</DrawerSection>
					</>
				) : null}
			</Drawer>
		</Card>
	);
}
